import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import WagerPanel from "@/components/WagerPanel";
import { toast } from "sonner";
import { Bomb, Gem, Coins, RotateCcw } from "lucide-react";
import { maybeCelebrate } from "@/lib/celebrate";
import { playSfx } from "@/lib/sfx";

const SIZE = 5;
const TOTAL = SIZE * SIZE;

function computeMultiplier(revealed, mines) {
  const safe = TOTAL - mines;
  const RTP = 0.30; // 70% house edge — mirrors backend
  let m = 1;
  for (let i = 0; i < revealed; i++) {
    const p = (safe - i) / (TOTAL - i);
    if (p <= 0) return m * RTP;
    m /= p;
  }
  return m * RTP;
}

export default function Minesweeper() {
  const { refresh } = useAuth();
  const nav = useNavigate();
  const [mines, setMines] = useState(3);
  const [session, setSession] = useState(null);
  const [bombs, setBombs] = useState(new Set());
  const [revealed, setRevealed] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [hitMine, setHitMine] = useState(false);

  const mult = useMemo(() => computeMultiplier(revealed.size, mines), [revealed, mines]);
  const potential = session ? Math.floor(session.wager * mult) : 0;

  const start = async (wager) => {
    setBusy(true);
    try {
      const { data } = await api.post("/games/wager", { game: "minesweeper", wager, config: { mines } });
      // Place mines client-side (server settles based on outcome)
      const idxs = Array.from({ length: TOTAL }, (_, i) => i);
      for (let i = idxs.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [idxs[i], idxs[j]] = [idxs[j], idxs[i]];
      }
      setBombs(new Set(idxs.slice(0, mines)));
      setRevealed(new Set());
      setSession(data);
      setResult(null);
      setHitMine(false);
      await refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Cannot start"); }
    finally { setBusy(false); }
  };

  const reveal = (i) => {
    if (result || hitMine || revealed.has(i)) return;
    if (bombs.has(i)) {
      setHitMine(true);
      setRevealed((s) => new Set(s).add(i));
      playSfx("lose");
      settle(true, revealed.size);
    } else {
      setRevealed((s) => new Set(s).add(i));
      playSfx("step");
    }
  };

  const settle = async (didHitMine, revealedCount) => {
    setBusy(true);
    try {
      const { data } = await api.post("/games/settle", {
        session_id: session.session_id,
        outcome: {
          revealed: revealedCount,
          mines,
          cashed_out: !didHitMine,
          hit_mine: didHitMine,
          total_cells: TOTAL,
        },
      });
      setResult(data);
      if (data.payout > 0) {
        toast.success(`+${data.payout.toLocaleString()} chips`);
        maybeCelebrate({ payout: data.payout, wager: session.wager });
        playSfx("cashOut");
        if (data.payout >= session.wager * 3) setTimeout(() => playSfx("megaWin"), 200);
      }
      else { toast.error(`No payout · lost ${session.wager}`); if (!didHitMine) playSfx("lose"); }
      await refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Settle failed"); }
    finally { setBusy(false); }
  };

  const cashout = () => settle(false, revealed.size);
  const restart = () => { setSession(null); setResult(null); setHitMine(false); };

  return (
    <div className="space-y-6" data-testid="minesweeper-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="pill pill-gold inline-flex items-center gap-1"><Bomb className="w-3.5 h-3.5" /> Minesweeper Tactical</div>
          <h1 className="font-display text-3xl mt-2">Reveal or retreat</h1>
        </div>
        <button onClick={() => nav("/lobby")} className="btn-ghost text-sm" data-testid="back-to-lobby">Back to lobby</button>
      </div>

      {!session ? (
        <div className="grid lg:grid-cols-2 gap-4">
          <div className="vexis-card p-5">
            <div className="pill">Risk</div>
            <div className="mt-2 font-display text-3xl">{mines} mines</div>
            <div className="text-xs text-slate-500 mt-1">out of 25 cells</div>
            <input type="range" min={1} max={10} value={mines} onChange={(e) => setMines(parseInt(e.target.value))}
              className="w-full mt-4 accent-[color:var(--gold)]" data-testid="minesweeper-mines-slider" />
            <div className="mt-3 grid grid-cols-4 gap-2 text-xs">
              {[1,3,5,7].map((n) => (
                <button key={n} onClick={() => setMines(n)} className={`px-2 py-1.5 rounded-full border ${mines===n ? "border-[color:var(--gold)] text-[color:var(--gold)]" : "border-white/10 text-slate-400"}`} data-testid={`mines-preset-${n}`}>{n} mines</button>
              ))}
            </div>
            <div className="mt-4 text-xs text-slate-400">First safe pick multiplier: <span className="font-mono text-[color:var(--gold)]">{(computeMultiplier(1, mines)).toFixed(2)}×</span></div>
          </div>
          <WagerPanel game="Minesweeper" onStart={start} busy={busy} />
        </div>
      ) : (
        <div className="grid lg:grid-cols-[1fr_320px] gap-6 items-start">
          <div className={`vexis-card p-4 sm:p-6 ${result ? (result.payout > 0 ? "win-glow" : "loss-glow") : ""}`}>
            <div className="grid grid-cols-5 gap-2 sm:gap-3">
              {Array.from({ length: TOTAL }).map((_, i) => {
                const isRev = revealed.has(i);
                const isBomb = bombs.has(i);
                const showBomb = (hitMine || result) && isBomb;
                return (
                  <button key={i} onClick={() => reveal(i)} disabled={isRev || hitMine || !!result}
                    data-testid={`mine-cell-${i}`}
                    className={`tile aspect-square text-xl ${isRev && !isBomb ? "bg-[rgba(16,185,129,0.15)] border border-emerald-500/50 text-emerald-400" : showBomb ? "bg-[rgba(239,68,68,0.15)] border border-red-500/60 text-red-400" : "bg-[#0E1218] border border-white/10 hover:border-white/25"}`}>
                    {isRev && !isBomb && <Gem className="w-4 h-4" />}
                    {showBomb && <Bomb className="w-4 h-4" />}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="space-y-3">
            <div className="vexis-card p-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><div className="text-slate-500 text-xs font-mono uppercase">Revealed</div><div className="font-display text-xl">{revealed.size}</div></div>
                <div><div className="text-slate-500 text-xs font-mono uppercase">Mines</div><div className="font-display text-xl">{mines}</div></div>
                <div><div className="text-slate-500 text-xs font-mono uppercase">Multiplier</div><div className="font-display text-xl text-[color:var(--gold)]">{mult.toFixed(2)}×</div></div>
                <div><div className="text-slate-500 text-xs font-mono uppercase">Potential</div><div className="font-display text-xl flex items-center gap-1"><Coins className="w-4 h-4 text-[color:var(--gold)]" />{potential.toLocaleString()}</div></div>
              </div>
            </div>

            {!result && (
              <button onClick={cashout} disabled={revealed.size === 0 || busy}
                className="btn-gold w-full inline-flex justify-center items-center gap-2" data-testid="minesweeper-cashout-button">
                Cash out · {potential.toLocaleString()} chips
              </button>
            )}

            {result && (
              <div className={`vexis-card p-4 ${result.payout > 0 ? "win-glow" : "loss-glow"}`} data-testid="minesweeper-result">
                {result.payout >= session.wager * 2 && (
                  <div className="pill pill-gold mb-2 inline-flex" data-testid="minesweeper-mega">✦ Mega Win · {(result.payout / session.wager).toFixed(2)}×</div>
                )}
                <div className={`font-display text-2xl ${result.payout > 0 ? "text-emerald-400" : "text-red-400"}`}>{result.payout > 0 ? "Cashed out" : hitMine ? "Boom" : "No payout"}</div>
                <div className="text-sm mt-1 text-slate-300">Payout · <span className="font-mono">{result.payout}</span> chips</div>
                <button onClick={restart} className="btn-gold w-full mt-4 inline-flex justify-center items-center gap-2" data-testid="minesweeper-play-again"><RotateCcw className="w-4 h-4" /> Play again</button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
